import type { ExportTemplateColumn } from '../types/export.types';

/**
 * The columns an export will emit, resolved from the run's own configuration.
 *
 * `unmatched` is the honest half of this: the batch's attribute keys and the
 * connector's column keys are separate vocabularies that overlap heavily but
 * not completely (scoring and Meta-only attributes have no GMC column). Naming
 * what could not be mapped is what stops "we defaulted to your batch" from
 * quietly meaning "we defaulted to most of your batch".
 */
export interface ColumnSelection {
  /** Column keys to export, in the connector's own declaration order. */
  selected: string[];
  /** Batch attribute keys with no matching connector column. */
  unmatched: string[];
}

/**
 * The connector's identifier columns (`id`, `retailer_id`).
 *
 * They are part of every default selection because a feed cannot be ingested
 * without them, but they are not forced: the picker lets them be unchecked like
 * any other column.
 */
export function identifierKeys(columns: ExportTemplateColumn[]): string[] {
  return columns.filter((column) => column.identifier).map((column) => column.key);
}

/**
 * Whether a column carries our own `product_id` — the Lily UUID, not the
 * retailer's GMC/Meta id. It is in every file (the server prepends it for
 * integration-service to join on) but it means nothing to a merchandiser
 * reading a preview, so no default selection includes it and the grids hide it
 * unless it is chosen explicitly. Detected by its backing column, not its key:
 * `id` is also what a Meta Commerce upload calls the retailer SKU.
 */
export function isInternalIdColumn(column: ExportTemplateColumn): boolean {
  return column.columns.length === 1 && column.columns[0] === 'product_id';
}

/**
 * The column keys a preview or browse grid draws: the selection, plus every
 * identifier the template always writes that is not our internal id.
 *
 * The picker shows those identifiers as always included, so a grid that drew
 * only the selection hid them — on `gmc-merchant-center-upload` that was `id`,
 * the Merchant Center id and the file's only identifier, because the
 * connector's `id` column is backed by `product_id` and read as internal. The
 * template's `identifierColumns` says what `id` actually is under it. Without
 * that field (an older service) this is the selection alone, as before.
 */
export function shownColumnKeys(
  selected: readonly string[],
  template: { identifiers?: string[]; identifierColumns?: Record<string, string[]> } | undefined,
): Set<string> {
  const shown = new Set(selected);
  const sources = template?.identifierColumns;
  if (!sources) return shown;
  for (const key of template.identifiers ?? []) {
    const columns = sources[key];
    const internal = columns?.length === 1 && columns[0] === 'product_id';
    if (columns && !internal) shown.add(key);
  }
  return shown;
}

/**
 * The columns a GMC export starts with when the run's own attribute list is unknown (a full
 * catalog spans many runs; older runs never recorded theirs): the external id plus the content
 * Lily enriches — the same set the Google layout ships. `id` (our product id) is not listed:
 * the server writes it into every file regardless. Meta keeps the every-column fallback.
 */
export const GMC_DEFAULT_FIELDS: readonly string[] = [
  'retailer_id',
  'title',
  'structured_title',
  'description',
  'structured_description',
  'product_highlight',
  'product_detail',
  'color',
  'material',
  'pattern',
  'google_product_category',
  'product_type',
];

/** The connector's standard starting columns, or `undefined` = every column. */
export function standardDefaultFields(connector: string | null | undefined) {
  return connector === 'GMC' ? GMC_DEFAULT_FIELDS : undefined;
}

/**
 * The picker's initial state: the batch's enriched attributes when we know
 * them, else the connector's standard set (`fallbackKeys`), else every column.
 *
 * Defaulting to the batch's selection is the point — an operator who enriched
 * four attributes does not want 52 columns, 48 of them empty. But the fallback
 * has to stay "everything", because three of the four export entry points have
 * no batch header to read and today's behaviour for them is all columns.
 *
 * A batch selection that maps to no columns at all also falls back to
 * everything rather than to an identifiers-only file: that would be an export
 * with no content, which is never what was wanted.
 */
export function resolveDefaultSelection({
  columns,
  batchAttributeKeys,
  fallbackKeys,
}: {
  columns: ExportTemplateColumn[];
  batchAttributeKeys: readonly string[] | undefined;
  /** Used when `batchAttributeKeys` is empty — see `standardDefaultFields`. Never reported as
   *  `unmatched`: those are the run's own attributes, not a standard list. */
  fallbackKeys?: readonly string[] | undefined;
}): ColumnSelection {
  const allKeys = columns.map((column) => column.key);
  // "Everything" still leaves the internal id out — see `isInternalIdColumn`.
  const shownKeys = columns.filter((column) => !isInternalIdColumn(column)).map((c) => c.key);
  if (!batchAttributeKeys || batchAttributeKeys.length === 0) {
    if (fallbackKeys && fallbackKeys.length > 0) {
      const wantedFallback = new Set(fallbackKeys);
      const ids = new Set(identifierKeys(columns));
      const picked = shownKeys.filter((key) => ids.has(key) || wantedFallback.has(key));
      // Only when it leaves real content: a connector config without these columns keeps "all".
      if (picked.some((key) => !ids.has(key))) return { selected: picked, unmatched: [] };
    }
    return { selected: shownKeys, unmatched: [] };
  }

  const available = new Set(allKeys);
  const wanted = new Set(batchAttributeKeys);
  const unmatched = [...wanted].filter((key) => !available.has(key));
  const identifiers = new Set(identifierKeys(columns));

  // Order comes from `columns`, never from the batch's array — the server emits
  // its own declaration order, so deriving it from the request would let the
  // picker imply a layout the file does not have.
  const selected = shownKeys.filter((key) => identifiers.has(key) || wanted.has(key));

  const hasContent = selected.some((key) => !identifiers.has(key));
  if (!hasContent) return { selected: shownKeys, unmatched };

  return { selected, unmatched };
}

/**
 * The `fields` value for the wire, or `null` for "every column".
 *
 * Sending `null` when everything is selected is deliberate rather than tidy:
 * `null`, `[]` and absent are all "every field" to the server, so a full
 * selection stays byte-identical to the request we sent before a picker
 * existed, and a future column added to the connector config is picked up
 * instead of being frozen out by a hardcoded list.
 */
export function selectionToFields(
  selected: readonly string[],
  columns: ExportTemplateColumn[],
): string[] | null {
  if (selected.length >= columns.length) return null;
  const chosen = new Set(selected);
  return columns.filter((column) => chosen.has(column.key)).map((column) => column.key);
}

/**
 * Whether a selection would export nothing but identifier columns.
 *
 * The server reads a `fields` list naming only identifiers as literally that —
 * `delivery_fields(['id','retailer_id'])` resolves to those two columns, while
 * an EMPTY list means every column. So clearing the picker does not mean "no
 * filter", it means a two-column file of ids. Callers use this to make that
 * outcome deliberate rather than a click away from `Clear`.
 *
 * False for an empty column list: nothing is known yet, so nothing is claimed.
 */
export function isIdentifierOnlySelection(
  selected: readonly string[],
  columns: ExportTemplateColumn[],
): boolean {
  if (columns.length === 0) return false;
  const identifiers = new Set(identifierKeys(columns));
  return selected.every((key) => identifiers.has(key));
}

/** One row of the downloadable column manifest. */
export interface ColumnManifestRow extends Record<string, unknown> {
  key: string;
  label: string;
  kind: string;
  sourceColumns: string;
  identifier: string;
  encoding: string;
}

/**
 * The selected columns as rows for a CSV the operator can hand to a client or a
 * feed vendor: the wire name that becomes the header, the human label, the
 * enrichment column behind it, and the encoding this template applies.
 *
 * Built from the same config the picker and the preview read, so the manifest
 * cannot drift from the file it describes.
 */
export function buildColumnManifest({
  columns,
  selected,
  transformations,
}: {
  columns: ExportTemplateColumn[];
  selected: readonly string[];
  /** The active template's `{ columnKey: transformationCode }` delta. */
  transformations: Record<string, string>;
}): ColumnManifestRow[] {
  const chosen = new Set(selected);
  return columns
    .filter((column) => chosen.has(column.key))
    .map((column) => ({
      key: column.key,
      label: column.label,
      kind: column.kind,
      sourceColumns: column.columns.join(' + '),
      identifier: column.identifier ? 'yes' : 'no',
      // Absent from the delta means the template leaves the column alone.
      encoding: transformations[column.key] ?? 'PASSTHROUGH',
    }));
}

/**
 * The expansion that makes a template fold a descriptor into `description`.
 *
 * Published on the template config precisely so this branch keys off the
 * BEHAVIOUR and not off the code `'gmc-feed'` — a future feed template gets
 * the same handling with no change here.
 */
export const DESCRIPTOR_ABSORB_EXPANSION = 'DESCRIPTOR_INTO_DESCRIPTION';

/** The two descriptor columns. Mutually exclusive on a template that absorbs. */
export const DESCRIPTOR_KEYS = ['search_descriptors', 'phrase_descriptors'] as const;

/**
 * The two columns that pre-join `description` to a descriptor.
 *
 * Hidden on a template that absorbs: `description` already carries that
 * combination there, so the server drops these, and offering a column the file
 * will not contain is worse than not offering it.
 */
export const COMBINED_DESCRIPTOR_KEYS = [
  'description_search_descriptors',
  'description_phrase_descriptors',
] as const;

/**
 * Template codes known to absorb, for a service that does not publish
 * `expansions` yet.
 *
 * TEMPORARY, and not a guess: `gmc-feed` carries
 * `DESCRIPTOR_INTO_DESCRIPTION` in the deployed config (version 5) and folds
 * the descriptor server-side today — the field simply is not on the DTO, so
 * the UI cannot read what the server is already doing. Without this the
 * control is invisible against the live backend.
 *
 * Delete once every environment serves `expansions`: the branch below prefers
 * the published list whenever it is present, so removing this changes nothing
 * on an up-to-date service.
 */
const ABSORBING_TEMPLATE_CODES = new Set(['gmc-feed']);

/**
 * Whether this template folds the selected descriptor into `description`.
 *
 * Prefers the published `expansions`; falls back to the code only when the
 * field is absent entirely, which is how a service predating it presents.
 */
export function absorbsDescriptors(
  template: { code?: string; expansions?: string[] } | undefined,
): boolean {
  if (!template) return false;
  if (template.expansions) return template.expansions.includes(DESCRIPTOR_ABSORB_EXPANSION);
  return Boolean(template.code && ABSORBING_TEMPLATE_CODES.has(template.code));
}

/**
 * The columns to offer for this template.
 *
 * On a feed template the descriptors are chosen by the radio beside the
 * template, so the picker must not offer the same choice a second way — two
 * controls for one value is how they come to disagree. The pre-joined pair is
 * withheld too: the server drops those when absorbing, and offering a column
 * the file will not contain is worse than not offering it.
 */
export function visibleColumns(
  columns: ExportTemplateColumn[],
  template: { code?: string; expansions?: string[]; excludeFields?: string[] } | undefined,
): ExportTemplateColumn[] {
  const offered = columns.filter((column) => !isExcludedBy(template, column.key));
  if (!absorbsDescriptors(template)) return offered;
  const hidden = new Set<string>([...COMBINED_DESCRIPTOR_KEYS, ...DESCRIPTOR_KEYS]);
  return offered.filter((column) => !hidden.has(column.key));
}

/**
 * Whether the template leaves this column out of every file (`excludeFields`): `meta-raw`
 * excludes the derived `fb_product_category` / `additional_variant_attribute`, the Meta
 * channel templates the fields Meta never stores. The connector's column list is the same
 * for every template, so without this the picker offered — and a run that filled them
 * pre-selected — columns the server then refused ("Unknown META fields").
 */
function isExcludedBy(template: { excludeFields?: string[] } | undefined, key: string): boolean {
  return template?.excludeFields?.includes(key) ?? false;
}

/**
 * Coerce a selection into one the template can actually serve: no column it excludes
 * (`excludeFields`) and, on a template that absorbs, no stale descriptor pair.
 *
 * Two ways a selection goes stale without the operator touching a checkbox:
 * switching template with both descriptors already ticked (the server rejects
 * that pair with a 400), and carrying a combined column into a template that
 * hides it (it would be sent, silently dropped, and counted in the badge).
 * Both are fixed here rather than at the checkbox, because neither originates
 * at one.
 *
 * Keeps the FIRST descriptor in the connector's declaration order, so the
 * result does not depend on the order the operator happened to click.
 */
export function normaliseDescriptorSelection(
  selected: string[],
  template: { code?: string; expansions?: string[]; excludeFields?: string[] } | undefined,
): string[] {
  // A column ticked under another template that this one excludes — see `isExcludedBy`.
  selected = selected.filter((key) => !isExcludedBy(template, key));
  if (!absorbsDescriptors(template)) return selected;
  const hidden = new Set<string>(COMBINED_DESCRIPTOR_KEYS);
  let seenDescriptor = false;
  return selected.filter((key) => {
    if (hidden.has(key)) return false;
    if (!(DESCRIPTOR_KEYS as readonly string[]).includes(key)) return true;
    if (seenDescriptor) return false;
    seenDescriptor = true;
    return true;
  });
}

/**
 * Apply one checkbox toggle, enforcing the either/or between descriptors.
 *
 * Radio-LIKE, not a radio: turning the chosen one off leaves neither selected,
 * which is a real choice — `description` then exports on its own. A true radio
 * group has no such state, which is why this is a toggle with a side effect
 * rather than a `<RadioGroup>`.
 *
 * Returns a NEW set; the caller owns the draft.
 */
export function applyDescriptorChoice({
  draft,
  key,
  checked,
  template,
}: {
  draft: Set<string>;
  key: string;
  checked: boolean;
  template: { expansions?: string[] } | undefined;
}): Set<string> {
  const next = new Set(draft);
  if (checked) next.add(key);
  else next.delete(key);
  if (!checked || !absorbsDescriptors(template)) return next;
  if (!(DESCRIPTOR_KEYS as readonly string[]).includes(key)) return next;
  for (const other of DESCRIPTOR_KEYS) {
    if (other !== key) next.delete(other);
  }
  return next;
}

/** The descriptor a feed export appends to `description`, or none. */
export type DescriptorChoice = 'none' | (typeof DESCRIPTOR_KEYS)[number];

/**
 * Which descriptor the current selection appends — the radio's value.
 *
 * Derived from the selection rather than held beside it, so the radio and the
 * column set cannot disagree. `'none'` covers both "no descriptor selected"
 * and "a descriptor is selected but `description` is not", because neither
 * appends anything to the file.
 */
export function descriptorChoice(
  selected: string[],
  template: { code?: string; expansions?: string[] } | undefined,
): DescriptorChoice {
  if (!absorbsDescriptors(template) || !selected.includes('description')) return 'none';
  return DESCRIPTOR_KEYS.find((key) => selected.includes(key)) ?? 'none';
}

/**
 * Apply a radio choice to the selection.
 *
 * Selecting a descriptor also selects `description`: absorption folds one into
 * the other, so a descriptor without it appends to a column the file does not
 * contain — the server treats that as "nothing to absorb" and the operator
 * would see their choice silently do nothing.
 *
 * `'none'` clears the descriptors and LEAVES `description` alone: it was very
 * likely selected before any of this, and unselecting a column the operator
 * did not ask about would be a surprise.
 *
 * Order is preserved by rebuilding from *columns*, so a descriptor added here
 * lands in the connector's declaration order rather than at the end.
 */
export function withDescriptorChoice({
  selected,
  choice,
  columns,
}: {
  selected: string[];
  choice: DescriptorChoice;
  columns: ExportTemplateColumn[];
}): string[] {
  const next = new Set(selected);
  for (const key of DESCRIPTOR_KEYS) next.delete(key);
  if (choice !== 'none') {
    next.add(choice);
    next.add('description');
  }
  return columns.filter((column) => next.has(column.key)).map((column) => column.key);
}

/**
 * The columns a request may name while `enabledOptions` are on. A column with a
 * `requiresOption` the request does not switch on would be refused by the
 * server ("Unknown META fields"), so it is not offered, not defaulted to and
 * never sent. The batch pages send no options, so they pass an empty set.
 */
export function availableColumns(
  columns: ExportTemplateColumn[],
  enabledOptions: ReadonlySet<string>,
): ExportTemplateColumn[] {
  return columns.filter(
    (column) => !column.requiresOption || enabledOptions.has(column.requiresOption),
  );
}
